import { t } from '@/i18n';

/*
 * The join form, as data. No Vue, no fetch.
 *
 * The alphabet and the length mirror the API's domain/games.ts, and `displayNameMax` mirrors the
 * contract's Player displayName. openapi-typescript erases `minLength` and `maxLength`, so this
 * file and the backend's valibot schema are the only two places those constraints exist. When
 * they disagree, packages/contract/openapi.yaml wins.
 */

/** The full alphabet minus 0/O, 1/I/L and U — nothing here is ambiguous read aloud. */
export const GAME_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const GAME_CODE_LENGTH = 6;

export const JOIN_LIMITS = { displayNameMax: 40 } as const;

const GAME_CODE = new RegExp(`^[${GAME_CODE_ALPHABET}]{${GAME_CODE_LENGTH}}$`);

/**
 * Upper-cases, trims, drops the spaces and dashes people add to make a code readable — and takes
 * the code out of a **link**.
 *
 * A pool is shared by link, so a link is what somebody pastes: the box refused
 * `https://…/i/K7M2QP` with "that is not a code", which is true of the string and useless about
 * the situation. The last path segment is the code in every URL this app produces — `/i/:code`,
 * the one that actually gets shared, as well as `/join/:code`, `/game/:code` and `/host/:code` —
 * and a query or a fragment is not part of it.
 *
 * Normalising is still not validating: `isGameCode` is the gate, and anything that is not a link
 * comes back untouched so a refusal can still name what was typed.
 */
export const normaliseGameCode = (value: string): string => {
  const trimmed = value.trim();
  const path = trimmed.includes('/')
    ? (trimmed.split(/[?#]/)[0] ?? '').replace(/\/+$/, '').split('/').pop()
    : trimmed;

  return (path ?? '').replace(/[\s-]/g, '').toUpperCase();
};

/** Shape only — says nothing about whether the game exists. */
export const isGameCode = (value: string): boolean => GAME_CODE.test(value);

export interface JoinForm {
  code: string;
  displayName: string;
}

export type JoinFieldErrors = Partial<Record<keyof JoinForm, string>>;

/**
 * The code comes from the link and the name from whoever the app already knows — a returning
 * player should not have to retype either, and joining is idempotent on the name.
 */
export const blankJoinForm = (code = '', displayName = ''): JoinForm => ({
  code: normaliseGameCode(code),
  displayName,
});

/**
 * The rules for a name, wherever one is typed — the join form and the settings screen. One copy,
 * because two would eventually disagree about the length and the join form would be the one that
 * found out.
 */
export function validateDisplayName(value: string): string | undefined {
  const displayName = value.trim();

  if (displayName.length === 0) return t('validation.name.required');
  if (displayName.length > JOIN_LIMITS.displayNameMax) return t('validation.name.tooLong');

  return undefined;
}

/**
 * Validated here so a mistyped code is a sentence next to the field rather than a 404 screen.
 * The backend validates the same rules regardless — this is courtesy, not security.
 */
export function validateJoin(form: JoinForm): JoinFieldErrors {
  const errors: JoinFieldErrors = {};

  const code = normaliseGameCode(form.code);
  if (code.length === 0) {
    errors.code = t('validation.code.required');
  } else if (!isGameCode(code)) {
    errors.code = t('validation.code.shape');
  }

  const displayName = validateDisplayName(form.displayName);
  if (displayName) errors.displayName = displayName;

  return errors;
}
